/* Size Fit Quiz - modal + step logic (visual only) */
(function () {
  /**
   * @param {HTMLElement} container
   */
  function initOne(container) {
    if (!container) return;
    const anyC = /** @type {any} */(container);
    if (anyC.__sfqInited) return;

    const modalEl = container.querySelector('[data-sfq-modal]');
    const overlayEl = container.querySelector('[data-sfq-overlay]');
    const openBtnEl = container.querySelector('[data-sfq-open]');
    const closeBtns = container.querySelectorAll('[data-sfq-close]');
    const stepsWrap = container.querySelector('[data-sfq-steps]');
    let steps = stepsWrap ? Array.from(stepsWrap.querySelectorAll('.sfq-step')) : [];
    let completeView = container.querySelector('[data-complete]');

    if (!(modalEl instanceof HTMLElement)) return;
    if (!(overlayEl instanceof HTMLElement)) return;
    if (!(openBtnEl instanceof HTMLElement)) return;
    if (!steps.length) return;

    // Cast to concrete HTMLElements for type safety in editors
    /** @type {HTMLElement} */ const modal = modalEl;
    /** @type {HTMLElement} */ const overlay = overlayEl;
    /** @type {HTMLElement} */ const openBtn = openBtnEl;

    let current = 0; // index in steps
    const answers = /** @type {Record<string, string>} */({});

    // --- Matrix data loader (expects window.SFQ_CONFIG.matrixUrl) ---
    /** @type {any|null} */ let matrixData = null;
    let matrixLoaded = false;
    async function loadMatrix() {
      if (matrixLoaded) return matrixData;
      try {
        const cfg = /** @type {any} */ (window).SFQ_CONFIG || {};
        const url = cfg.matrixUrl;
        if (!url) {
          console.warn('[SFQ] matrixUrl is not set in window.SFQ_CONFIG. Please set it to the asset_url of size-fit-matrix.json');
          matrixLoaded = true; // avoid repeated attempts
          return null;
        }
        console.debug('[SFQ] fetching matrix JSON from', url);
        const res = await fetch(url, { credentials: 'omit' });
        if (!res.ok) throw new Error('matrix fetch failed: ' + res.status);
        matrixData = await res.json();
        try {
          const brandCount = matrixData && typeof matrixData === 'object' ? Object.keys(matrixData).length : 0;
          console.debug('[SFQ] matrix loaded. brands =', brandCount, Object.keys(matrixData || {}));
          const fs = matrixData && matrixData._footSize;
          if (fs) {
            /** @type {string[]} */
            const lb = [];
            if (Array.isArray(fs.lengthBins)) {
              for (let i = 0; i < fs.lengthBins.length; i++) {
                const it = /** @type {any} */(fs.lengthBins[i]);
                lb.push(String(it && it.label));
              }
            }
            /** @type {string[]} */
            const gb = [];
            if (Array.isArray(fs.girthBins)) {
              for (let i = 0; i < fs.girthBins.length; i++) {
                const it = /** @type {any} */(fs.girthBins[i]);
                gb.push(String(it && it.label));
              }
            }
            console.debug('[SFQ] _footSize present', { lengthBins: lb, girthBins: gb, hasMap: !!fs.map });
          } else {
            console.debug('[SFQ] _footSize not present in matrix');
          }
        } catch {}
        matrixLoaded = true;
        return matrixData;
      } catch (e) {
        console.error('[SFQ] failed to load matrix:', e);
        matrixLoaded = true;
        return null;
      }
    }

    // --- Helper: compute a sample result string ---
    function computeResult() {
      // Matrix-based lookup when using existing shoe flow
      const i18n = /** @type {any} */(window).SFQ_I18N || {};
      if (answers.q1 === i18n.method_shoe || answers.q1 === '今履いているシューズから診断') {
        const brand = answers.brand || '';
        const sizeCm = answers.sizeCm || '';
        const fit = answers.q3 || answers.fit || '';
        if (matrixData && brand && sizeCm && fit) {
          const byBrand = matrixData[brand];
          const bySize = byBrand && byBrand[sizeCm];
          const v = bySize && bySize[fit];
          console.debug('[SFQ] computeResult lookup', { brand, sizeCm, fit, found: v });
          if (typeof v === 'string' && v) return v;
          // allow object form with label field in future
          if (v && typeof v === 'object' && typeof v.label === 'string' && v.label) return v.label;
          return i18n.no_data_found || '該当データが見つかりません';
        }
        console.warn('[SFQ] computeResult missing inputs or matrix', { hasMatrix: !!matrixData, brand, sizeCm, fit });
        return i18n.no_data_found || '該当データが見つかりません';
      }
      // Foot-size path -> lookup from matrix _footSize map
      if (answers.q1 === i18n.method_foot || answers.q1 === '足のサイズで診断') {
        const len = answers.q2 || '';
        const girth = answers.q3 || '';
        /** @param {string} s */
        const norm = (s) => String(s).replace(/[〜~]/g, '-').replace(/\s+/g, '');
        const nLen = norm(len);
        const nGirth = norm(girth);
        if (matrixData && matrixData._footSize && matrixData._footSize.map) {
          const m = matrixData._footSize.map;
          const row = m[nLen] || m[len];
          const out = row && (row[nGirth] || row[girth]);
          console.debug('[SFQ] foot-size lookup', { len, girth, nLen, nGirth, hasRow: !!row, found: out });
          if (typeof out === 'string' && out) return out;
          if (out && typeof out === 'object') {
            if (typeof out.label === 'string' && out.label) return out.label;
            if (typeof out.message === 'string' && out.message) return { message: out.message, type: String(out.type || 'note') };
          }
          return i18n.no_data_found || '該当データが見つかりません';
        }
        // Fallback (matrix not present)
        const parts = [nLen, nGirth].filter(Boolean).join('/');
        return parts || '';
      }
      return '';
    }

    // --- Helpers: build dropdown steps for the foot-size path ---
    /**
     * @param {HTMLElement} stepEl
     * @param {string} label
     * @param {string[]} options
     * @param {number} stepIndex 0-based index within steps array
     * @param {keyof typeof answers} answerKey
     */
    function configureDropdownStep(stepEl, label, options, stepIndex, answerKey) {
      if (!(stepEl instanceof HTMLElement)) return;
      // Ensure the question text is updated (use existing element)
      const q = stepEl.querySelector('.sfq-question');
      if (q) q.textContent = label;

      // Reuse existing dropdown wrapper if present to avoid duplicates
      let wrap = stepEl.querySelector('.sfq-dropdown-wrap');
      if (!(wrap instanceof HTMLElement)) {
        wrap = document.createElement('div');
        wrap.className = 'sfq-dropdown-wrap';
        // Replace options area inside the step (first-time only)
        const body = stepEl.querySelector('.sfq-options');
        if (body) {
          body.replaceWith(wrap);
        } else {
          // Insert before nav to keep stable order
          const nav = stepEl.querySelector('.sfq-nav');
          if (nav && nav.parentNode) nav.parentNode.insertBefore(wrap, nav);
          else stepEl.appendChild(wrap);
        }
      }

      // Remove any existing select element to avoid duplicates
      const existingSelect = wrap.querySelector('select');
      if (existingSelect) existingSelect.remove();

      // Render/select element
      const i18n = /** @type {any} */(window).SFQ_I18N || {};
      wrap.innerHTML = `
        <div class=\"sfq-select\">
          <select data-sfq-select>
            <option value="">${i18n.please_select || '選択してください'}</option>
            ${options.map((o) => `<option value=\"${o}\">${o}</option>`).join('')}
          </select>
        </div>
      `;

      // Bind change -> save + auto-advance (assign handler to avoid duplicates)
      const select = /** @type {HTMLSelectElement|null} */(wrap.querySelector('[data-sfq-select]'));
      if (select) {
        select.onchange = (e) => {
          const val = /** @type {HTMLSelectElement} */(e.target).value;
          answers[answerKey] = val || '';
          console.debug('[SFQ] answer set', { key: String(answerKey), val: answers[answerKey], stepIndex });
          if (val) {
            if (stepIndex < steps.length - 1) showStep(stepIndex + 1);
            else showComplete();
          }
        };
      }
    }

    // (moved) reload on close listener is attached inside bindCompletionActions()
    // サイズガイドのリンクを足長・足囲の測り方のステップに追加する
    /** @param {HTMLElement} stepEl */
    function ensureFootMeasureLink(stepEl) {
      if (!(stepEl instanceof HTMLElement)) return;
      const nav = stepEl.querySelector('.sfq-nav');
      if (!(nav instanceof HTMLElement)) return;
      // avoid duplicating the link
      if (nav.querySelector('[data-sfq-measure-link]')) return;
      const p = document.createElement('p');
      p.className = 'sfq-measure-note';
      const i18n = /** @type {any} */(window).SFQ_I18N || {};
      const cfg = /** @type {any} */(window).SFQ_CONFIG || {};
      const isEn = cfg.lang === 'en';
      const a = document.createElement('a');
      a.href = 'https://brightway-osaka.co.jp/pages/size-guide';
      a.target = '_blank';
      a.rel = 'noopener';
      a.textContent = i18n.measure_guide_link || 'こちら';
      a.setAttribute('data-sfq-measure-link', 'true');
      if (isEn) {
        // English: "please check here" (prefix + link at end)
        const prefix = document.createTextNode(i18n.measure_guide_prefix || 'Regarding how to measure foot length and girth, ');
        p.appendChild(prefix);
        p.appendChild(document.createTextNode(' '));
        p.appendChild(a);
      } else {
        // Japanese: "足長・足囲の測り方については こちら をご覧ください" (prefix + link + suffix)
        const prefix = document.createTextNode(i18n.measure_guide_prefix || '足長・足囲の測り方については');
        const tail = document.createTextNode(i18n.measure_guide_suffix || 'をご覧ください');
        p.appendChild(prefix);
        p.appendChild(a);
        p.appendChild(tail);
      }
      const backBtn = nav.querySelector('.sfq-back');
      if (backBtn instanceof HTMLElement) {
        nav.insertBefore(p, backBtn);
      } else {
        nav.appendChild(p);
      }
    }

    function configureFlowForFootSize() {
      (async () => {
        await loadMatrix();
        const fs = matrixData && matrixData._footSize;
        /** @type {string[]} */
        let footLengths = ['23.0cm','23.5cm','24.0cm','24.5cm','25.0cm','25.5cm','26.0cm','26.5cm','27.0cm'];
        if (fs && Array.isArray(fs.lengthBins)) {
          footLengths = [];
          for (let i = 0; i < fs.lengthBins.length; i++) {
            const it = /** @type {any} */ (fs.lengthBins[i]);
            footLengths.push(String(it && it.label));
          }
        }
        const i18n = /** @type {any} */(window).SFQ_I18N || {};
        const step2 = steps[1];
        if (step2 instanceof HTMLElement) {
          configureDropdownStep(step2, i18n.foot_length_question || '足長（mm）は？（※足長＝かかとからつま先までの長さ）', footLengths, 1, 'q2');
          ensureFootMeasureLink(step2);
        }
        /** @type {string[]} */
        let girths = ['E','EE','EEE'];
        if (fs && Array.isArray(fs.girthBins)) {
          girths = [];
          for (let i = 0; i < fs.girthBins.length; i++) {
            const it = /** @type {any} */ (fs.girthBins[i]);
            girths.push(String(it && it.label));
          }
        }
        const step3 = steps[2];
        if (step3 instanceof HTMLElement) {
          configureDropdownStep(step3, i18n.foot_girth_question || '足囲（mm）は？（※足囲＝ボールガースの寸法）', girths, 2, 'q3');
          ensureFootMeasureLink(step3);
        }

        // Dependent filtering: when a foot-length is selected, restrict girth options
        // to only those that produce a valid mapped size (exclude 'unavailable').
        if (fs && fs.map && (step2 instanceof HTMLElement) && (step3 instanceof HTMLElement)) {
          /** @param {string} s */
          const norm = (s) => String(s).replace(/[〜~]/g, '-').replace(/\s+/g, '');
          const step2Select = /** @type {HTMLSelectElement|null} */(step2.querySelector('[data-sfq-select]'));
          if (step2Select) {
            step2Select.addEventListener('change', () => {
              const lenLabel = step2Select.value || '';
              const nLen = norm(lenLabel);
              /** @type {string[]} */
              const filtered = [];
              const row = fs.map[nLen] || fs.map[lenLabel];
              if (row && typeof row === 'object') {
                for (const k in row) {
                  if (!Object.prototype.hasOwnProperty.call(row, k)) continue;
                  const v = row[k];
                  // include only actual size labels (strings) or objects with label
                  // exclude 'unavailable' message objects
                  if (typeof v === 'string' && v) {
                    filtered.push(k);
                  } else if (v && typeof v === 'object') {
                    if (typeof v.label === 'string' && v.label) filtered.push(k);
                    else if (v.type === 'unavailable') {
                      // skip unavailable
                    }
                  }
                }
              }
              // Fallback to full list if nothing matched (avoid empty dropdown)
              const nextOptions = filtered.length ? filtered : girths;
              // Reset previous answer and rebuild step3 dropdown with filtered options
              answers.q3 = '';
              configureDropdownStep(step3, i18n.foot_girth_question || '足囲（mm）は？（※足囲＝ボールガースの寸法）', nextOptions, 2, 'q3');
              ensureFootMeasureLink(step3);
              // Move to step3 automatically if a length is chosen
              if (lenLabel) showStep(2);
              try { console.debug('[SFQ] filtered girths for', lenLabel, '=>', nextOptions); } catch {}
            });
          }
        }
      })();
    }

    // --- Existing shoe flow: Step1 brand/model, Step2 size, Step3 fit ---
    /**
     * Replace step with button options (uses existing .sfq-options and global click handler)
     * @param {HTMLElement} stepEl
     * @param {string} label
     * @param {string[]} options
     */
    function setButtonOptions(stepEl, label, options) {
      if (!(stepEl instanceof HTMLElement)) return;
      const q = stepEl.querySelector('.sfq-question');
      if (q) q.textContent = label;
      // Remove dropdown wrap if previously set
      const dd = stepEl.querySelector('.sfq-dropdown-wrap');
      if (dd && dd.parentNode) dd.parentNode.removeChild(dd);
      // Ensure .sfq-options exists
      let opts = stepEl.querySelector('.sfq-options');
      if (!(opts instanceof HTMLElement)) {
        opts = document.createElement('div');
        opts.className = 'sfq-options';
        // Insert before nav to keep stable order
        const nav = stepEl.querySelector('.sfq-nav');
        if (nav && nav.parentNode) nav.parentNode.insertBefore(opts, nav);
        else stepEl.appendChild(opts);
      }
      // Render buttons
      opts.innerHTML = options.map((o) => `<button class="sfq-option" type="button" data-value="${o}">${o}</button>`).join('');
    }

    /** @param {string} brand */
    function getBrandSizeUnit(brand) {
      try {
        const byBrand = matrixData && brand ? matrixData[brand] : null;
        const unit = byBrand && typeof byBrand === 'object' ? byBrand._sizeUnit : null;
        return unit ? String(unit) : 'cm';
      } catch {
        return 'cm';
      }
    }

    /**
     * @param {string} brand
     * @param {string} sizeKey
     */
    function formatSizeLabel(brand, sizeKey) {
      const unit = getBrandSizeUnit(brand);
      if (unit === 'EU') return `EU ${sizeKey}`;
      if (unit === 'cm') return `${sizeKey}cm`;
      return `${sizeKey}${unit}`;
    }

    // Step2 renderer for size only (used when brand is uniquely determined)
    /**
     * @param {string} brand
     */
    function configureStep2Size(brand) {
      const step2 = steps[1];
      if (!(step2 instanceof HTMLElement)) return;
      const i18n = /** @type {any} */(window).SFQ_I18N || {};
      const q = step2.querySelector('.sfq-question');
      if (q) q.textContent = i18n.current_size_question || '今履いているサイズは？';
      // remove previous content types
      const dd = step2.querySelector('.sfq-dropdown-wrap');
      if (!(dd instanceof HTMLElement)) {
        const wrap = document.createElement('div');
        wrap.className = 'sfq-dropdown-wrap';
        const body = step2.querySelector('.sfq-options');
        if (body) body.replaceWith(wrap); else {
          const nav = step2.querySelector('.sfq-nav');
          if (nav && nav.parentNode) nav.parentNode.insertBefore(wrap, nav); else step2.appendChild(wrap);
        }
      }
      const wrap = /** @type {HTMLElement} */(step2.querySelector('.sfq-dropdown-wrap'));
      const unit = getBrandSizeUnit(brand);
      const unitLabel = unit === 'EU' ? 'EU' : unit;
      /** @type {string[]} */ const sizes = (matrixData && brand && matrixData[brand])
        ? Object.keys(matrixData[brand]).filter(k => !k.startsWith('_'))
        : [];
      const selectLabel = unitLabel === 'EU' ? (i18n.select_size_eu || 'サイズ(EU)を選択') : (i18n.select_size_cm || 'サイズ(cm)を選択');
      wrap.innerHTML = `
        <div class=\"sfq-select\">\n          <select data-sfq-size-step2>\n            <option value=\"\">${selectLabel}</option>\n            ${sizes.map(s => `<option value=\"${s}\">${formatSizeLabel(brand, s)}</option>`).join('')}\n          </select>\n        </div>`;
      const sizeSel = /** @type {HTMLSelectElement|null} */(wrap.querySelector('[data-sfq-size-step2]'));
      if (sizeSel) {
        sizeSel.onchange = (e) => {
          const v = /** @type {HTMLSelectElement} */(e.target).value || '';
          answers.sizeCm = v;
          console.debug('[SFQ] [Q2] size selected:', v);
          if (v) showStep(2); // advance to Q3
        };
      }
    }

    // Step3 renderer for fit only
    function configureStep3Fit() {
      const step3 = steps[2];
      if (!(step3 instanceof HTMLElement)) return;
      const i18n = /** @type {any} */(window).SFQ_I18N || {};
      const q = step3.querySelector('.sfq-question');
      if (q) q.textContent = i18n.current_fit_question || '今履いている靴はピッタリ？ ゆったり？';
      // remove dropdown if present
      const dd = step3.querySelector('.sfq-dropdown-wrap');
      if (dd && dd.parentNode) dd.parentNode.removeChild(dd);
      // ensure options container
      let opts = step3.querySelector('.sfq-options');
      if (!(opts instanceof HTMLElement)) {
        opts = document.createElement('div');
        opts.className = 'sfq-options';
        const nav = step3.querySelector('.sfq-nav');
        if (nav && nav.parentNode) nav.parentNode.insertBefore(opts, nav); else step3.appendChild(opts);
      }
      const fitChoices = [i18n.fit_perfect || 'ピッタリ', i18n.fit_roomy || 'ゆったり'];
      opts.innerHTML = fitChoices.map(f => `<button class="sfq-fit-btn" type="button" data-fit="${f}">${f}</button>`).join('');
      const fitBtns = Array.from(opts.querySelectorAll('.sfq-fit-btn'));
      fitBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          fitBtns.forEach(b => b.classList.remove('is-selected'));
          btn.classList.add('is-selected');
          const f = btn.getAttribute('data-fit') || '';
          if (f) answers.q3 = f;
          console.debug('[SFQ] [Q3] fit selected:', f);
          if (answers.sizeCm && answers.q3) showComplete();
        });
      });
    }

    // Render Step3 composite controls for a given brand (size cm + fit)
    /**
     * @param {string} brand
     */
    function configureStep3Composite(brand) {
      const step3 = steps[2];
      if (!(step3 instanceof HTMLElement)) return;
      // question label
      const i18n = /** @type {any} */(window).SFQ_I18N || {};
      const q = step3.querySelector('.sfq-question');
      const unit = getBrandSizeUnit(brand);
      const unitLabel = unit === 'EU' ? 'EU' : unit;
      const questionText = (i18n.size_and_fit_question || 'サイズ({{ unit }}) と 履き心地は？').replace('{{ unit }}', unitLabel);
      if (q) q.textContent = questionText;
      // remove prior dropdown if any
      const dd = step3.querySelector('.sfq-dropdown-wrap');
      if (dd && dd.parentNode) dd.parentNode.removeChild(dd);
      // ensure options container
      let opts = step3.querySelector('.sfq-options');
      if (!(opts instanceof HTMLElement)) {
        opts = document.createElement('div');
        opts.className = 'sfq-options';
        const nav = step3.querySelector('.sfq-nav');
        if (nav && nav.parentNode) nav.parentNode.insertBefore(opts, nav);
        else step3.appendChild(opts);
      }
      // sizes for brand
      /** @type {string[]} */ const sizes = (matrixData && brand && matrixData[brand])
        ? Object.keys(matrixData[brand]).filter(k => !k.startsWith('_'))
        : [];
      console.debug('[SFQ] render Step3 for brand:', brand, 'sizes:', sizes);
      if (!brand) console.warn('[SFQ] Step3 rendered without brand selection yet');
      if (brand && (!sizes || sizes.length === 0)) console.warn('[SFQ] No sizes found for brand in matrix:', brand);
      const fitChoices = [i18n.fit_perfect || 'ピッタリ', i18n.fit_roomy || 'ゆったり'];
      const selectLabel = unitLabel === 'EU' ? (i18n.select_size_eu || 'サイズ(EU)を選択') : (i18n.select_size_cm || 'サイズ(cm)を選択');
      // NOTE: fit buttons intentionally do NOT have class 'sfq-option' to avoid triggering the global step handler
      opts.innerHTML = `
        <div class="sfq-composite">
          <div class="sfq-select">
            <select data-sfq-size>
              <option value="">${selectLabel}</option>
              ${sizes.map(s => `<option value="${s}">${formatSizeLabel(brand, s)}</option>`).join('')}
            </select>
          </div>
          <div class="sfq-fit" role="group" aria-label="${i18n.fit_preference || '履き心地'}">
            ${fitChoices.map(f => `<button class="sfq-fit-btn" type="button" data-fit="${f}">${f}</button>`).join('')}
          </div>
        </div>`;

      const sizeSel = /** @type {HTMLSelectElement|null} */(opts.querySelector('[data-sfq-size]'));
      const fitBtns = Array.from(opts.querySelectorAll('.sfq-fit-btn'));

      function updateAndMaybeFinish() {
        if (answers.sizeCm && answers.q3) {
          showComplete();
        } else if (answers.sizeCm && !answers.q3) {
          console.warn('[SFQ] size selected but fit not yet chosen. Please choose ピッタリ or ゆったり');
        } else if (!answers.sizeCm && answers.q3) {
          console.warn('[SFQ] fit selected but size not yet chosen. Please choose サイズ(cm)');
        }
      }

      sizeSel && (sizeSel.onchange = (e) => {
        const chosen = /** @type {HTMLSelectElement} */(e.target).value || '';
        if (chosen) answers.sizeCm = chosen; // size(cm)
        console.debug('[SFQ] size selected:', chosen);
        updateAndMaybeFinish();
      });

      fitBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          fitBtns.forEach(b => b.classList.remove('is-selected'));
          btn.classList.add('is-selected');
          const f = btn.getAttribute('data-fit') || '';
          if (f) answers.q3 = f; // fit
          console.debug('[SFQ] fit selected:', f);
          updateAndMaybeFinish();
        });
      });
    }

    function configureFlowForExistingShoe() {
      // Build from matrix data: Step1 brand buttons, Step2 size, Step3 fit
      const step1 = steps[0];
      (async () => {
        await loadMatrix();
        let brandEntries = (matrixData && typeof matrixData === 'object') ? Object.entries(matrixData) : [];
        // Exclude internal underscore-prefixed blocks like _footSize
        brandEntries = brandEntries.filter(([name]) => !String(name).startsWith('_'));
        const brands = brandEntries.map(([name]) => name);
        // Step1: brands with optional image below label
        if (step1 instanceof HTMLElement) {
          const i18n = /** @type {any} */(window).SFQ_I18N || {};
          const q = step1.querySelector('.sfq-question');
          if (q) q.textContent = i18n.brand_question || 'ブランド/モデルは？';
          // Ensure .sfq-options exists
          let opts = step1.querySelector('.sfq-options');
          if (!(opts instanceof HTMLElement)) {
            opts = document.createElement('div');
            opts.className = 'sfq-options';
            const nav = step1.querySelector('.sfq-nav');
            if (nav && nav.parentNode) nav.parentNode.insertBefore(opts, nav); else step1.appendChild(opts);
          }
          // Mark as brand grid for CSS targeting on mobile
          opts.classList.add('sfq-options--brands');
          opts.innerHTML = brandEntries.map(([name, data]) => {
            try {
              const img = (data && (data._image || data.image)) ? String(data._image || data.image) : '';
              const imgTag = img ? `<div class="sfq-option__thumb"><img src="${img}" alt="${name}" loading="lazy"></div>` : '';
              // Split label at the first space (ASCII or full-width) -> brand (left) + model (right)
              const idxAscii = name.indexOf(' ');
              const idxFull  = name.indexOf('　'); // U+3000 full-width space
              const idx = (idxAscii >= 0 && idxFull >= 0)
                ? Math.min(idxAscii, idxFull)
                : (idxAscii >= 0 ? idxAscii : idxFull);
              let labelHtml = name;
              if (idx >= 0) {
                const brand = name.slice(0, idx).trim();
                const model = name.slice(idx + 1).trim();
                labelHtml = `${brand}<br>${model}`;
              }
              return `<button class="sfq-option" type="button" data-value="${name}">
                <div class=\"sfq-option__label\">${labelHtml}</div>
                ${imgTag}
              </button>`;
            } catch {
              return `<button class=\"sfq-option\" type=\"button\" data-value=\"${name}\">${name}</button>`;
            }
          }).join('');
        }
        // Initialize Step2 and Step3 for the first brand (will update when user selects)
        const initialBrand = answers.brand || (brands[0] || '');
        answers.brand = initialBrand;
        configureStep2Size(initialBrand);
        configureStep3Fit();
      })();
    }

    // --- Portalize to body to avoid stacking context issues ---
    /** @type {Comment | null} */ let overlayPlaceholder = null;
    /** @type {Comment | null} */ let modalPlaceholder = null;
    let isPortaled = false;
    /** @type {HTMLDialogElement | null} */ let dialog = null;

    function portalizeToBody() {
      if (isPortaled) return;
      // Create placeholders to keep original position if needed later
      if (!overlayPlaceholder && overlay.parentNode) {
        overlayPlaceholder = document.createComment('sfq-overlay-placeholder');
        overlay.parentNode.insertBefore(overlayPlaceholder, overlay);
      }
      if (!modalPlaceholder && modal.parentNode) {
        modalPlaceholder = document.createComment('sfq-modal-placeholder');
        modal.parentNode.insertBefore(modalPlaceholder, modal);
      }
      // Create a <dialog> and move the modal content inside it
      const content = modal.querySelector('.sfq-modal__content');
      dialog = document.createElement('dialog');
      dialog.className = 'sfq-dialog';
      if (content) {
        // Clone the content to avoid moving the original
        const clonedContent = content.cloneNode(true);
        dialog.appendChild(clonedContent);
      }
      document.body.appendChild(dialog);
      // Close when clicking on the backdrop (native: target === dialog)
      dialog.addEventListener('click', (e) => {
        if (e.target === dialog) hideModal();
      });
      // Update references to use the cloned elements in dialog
      const dialogStepsWrap = dialog.querySelector('[data-sfq-steps]');
      steps = dialogStepsWrap ? Array.from(dialogStepsWrap.querySelectorAll('.sfq-step')) : [];
      completeView = dialog.querySelector('[data-complete]');
      // Re-bind all interactions to the cloned nodes
      bindStepHandlers();
      bindCompletionActions();
      
      // Completely remove the original overlay and modal from DOM
      if (overlay instanceof HTMLElement && overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
      if (modal instanceof HTMLElement && modal.parentNode) {
        modal.parentNode.removeChild(modal);
      }
      isPortaled = true;
    }

    /** @param {number} index */
    function showStep(index) {
      const safe = Math.max(0, Math.min(index, steps.length - 1));
      current = safe;
      steps.forEach((el, i) => {
        if (el instanceof HTMLElement) el.hidden = i !== safe;
      });
      if (completeView instanceof HTMLElement) completeView.hidden = true;
    }

    function showComplete() {
      steps.forEach((el) => { if (el instanceof HTMLElement) el.hidden = true; });
      if (completeView instanceof HTMLElement) {
        // Write result text
        const target = completeView.querySelector('[data-sfq-result]');
        if (target) {
          const res = computeResult();
          try { console.debug('[SFQ] showComplete result', { type: typeof res, res }); } catch {}
          const sentenceP = /** @type {HTMLElement|null} */(target.closest('p'));
          const saveBtnEl = /** @type {HTMLElement|null} */(completeView.querySelector('[data-sfq-save]'));
          const loginNote = /** @type {HTMLElement|null} */(completeView.querySelector('.sfq-login-note'));
          // Prepare alternative container to show messages when sentence is hidden
          let alt = /** @type {HTMLElement|null} */(completeView.querySelector('.sfq-alt'));
          if (!(alt instanceof HTMLElement)) {
            alt = document.createElement('div');
            alt.className = 'sfq-alt';
            // Insert before the sentence paragraph for consistent layout
            if (sentenceP && sentenceP.parentNode) sentenceP.parentNode.insertBefore(alt, sentenceP);
            else completeView.insertBefore(alt, completeView.firstChild);
          }
          if (res && typeof res === 'object' && 'message' in res) {
            const type = /** @type {{message:string,type?:string}} */(res).type || 'note';
            const msg = /** @type {{message:string}} */(res).message || '';
            // Hide sentence and save button when unavailable
            if (type === 'unavailable') {
              if (sentenceP) sentenceP.hidden = true;
              if (saveBtnEl) saveBtnEl.hidden = true;
              if (loginNote) loginNote.hidden = true;
              // Render message into alt container (visible)
              alt.hidden = false;
              const i18n = /** @type {any} */(window).SFQ_I18N || {};
              alt.innerHTML = `
                <div class="sfq-note sfq-note--${type}" role="status" aria-live="polite" style="font-size:13px;padding:12px 14px;border-radius:8px;border:1px solid #e2e8f0;background:#f8fafc;color:#0f172a;line-height:1.7;">
                  <div style="font-weight:600;margin-bottom:6px;">${i18n.availability || 'お取り扱いについて'}</div>
                  <div>${msg.replace(/\n/g, '<br>')}</div>
                </div>`;
              // Add spacing below the message card
              alt.style.marginBottom = '20px';
              // Ensure target (inside hidden sentence) does not hold conflicting content
              target.textContent = '';
              // Unhide completion view now that alt message is ready
              if (completeView instanceof HTMLElement) completeView.hidden = false;
              try { console.debug('[SFQ] showComplete -> unavailable: unhide completeView'); } catch {}
              return; // early
            }
            // Non-unavailable object -> show as plain text in sentence target
            if (alt) { alt.hidden = true; alt.innerHTML = ''; alt.style.marginBottom = ''; }
            target.textContent = String((/** @type {any} */(res)).label || (/** @type {any} */(res)).message || '');
          } else {
            // Restore visibility for normal cases
            if (sentenceP) sentenceP.hidden = false;
            if (saveBtnEl) saveBtnEl.hidden = false;
            if (loginNote) loginNote.hidden = (/** @type {any} */(window).SFQ_CONFIG?.customerId ? true : loginNote.hidden);
            if (alt) { alt.hidden = true; alt.innerHTML = ''; alt.style.marginBottom = ''; }
            target.textContent = String(res || '');
          }
        }
        completeView.hidden = false;
        // Ensure completion actions are bound
        bindCompletionActions();
      }
    }

    // Bind save action in completion view
    function bindCompletionActions() {
      const d = dialog; // capture to satisfy type checker
      if (!d) return;
      // Reload page when dialog closes (via close button, X, ESC, or backdrop)
      d.addEventListener('close', () => {
        try { window.location.reload(); } catch (_) {}
      }, { once: true });
      const saveBtn = d.querySelector('[data-sfq-save]');
      if (saveBtn instanceof HTMLElement) {
        // Avoid multiple listeners
        saveBtn.onclick = async () => {
          const cr = computeResult();
          let resultText = '';
          if (typeof cr === 'string') {
            resultText = cr;
          } else if (cr && typeof cr === 'object') {
            const anyCr = /** @type {any} */(cr);
            resultText = String(anyCr.label || anyCr.message || '');
          }
          const detail = {
            result: resultText,
            answers: { ...answers }
          };
          d.dispatchEvent(new CustomEvent('sfq:save', { detail, bubbles: true }));
          // If global config is available, post to API as well
          try {
            const cfg = /** @type {any} */ (window).SFQ_CONFIG || {};
            const endpoint = cfg.endpoint;
            const shopDomain = cfg.shopDomain;
            const customerId = cfg.customerId;
            // Require login to save: reveal inline note and stop
            if (!customerId) {
              if (completeView instanceof HTMLElement) {
                const note = completeView.querySelector('.sfq-login-note');
                if (note instanceof HTMLElement) {
                  note.hidden = false;
                  note.setAttribute('tabindex', '-1');
                  note.focus();
                }
              }
              return;
            }
            if (endpoint && shopDomain && customerId) {
              // Prepare inline status area
              let statusEl = completeView && completeView.querySelector('.sfq-save-status');
              if (!(statusEl instanceof HTMLElement) && completeView instanceof HTMLElement) {
                statusEl = document.createElement('p');
                statusEl.className = 'sfq-save-status';
                completeView.appendChild(statusEl);
              }
              // Disable button during request
              saveBtn.setAttribute('disabled', 'true');
              if (statusEl instanceof HTMLElement) {
                statusEl.textContent = '';
                statusEl.removeAttribute('data-state');
              }

              try {
                const res = await fetch(endpoint, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    shopDomain,
                    customerId,
                    result: detail.result,
                    answers: detail.answers,
                    // Append customer metafield write intent
                    customerMetafields: [
                      {
                        namespace: 'custom',
                        key: 'right_size_option',
                        type: 'list.single_line_text_field',
                        // Store the three answers in order: brand/model, size(cm), fit
                        values: [
                          detail.answers.brand || '',
                          detail.answers.sizeCm || '',
                          detail.answers.q3 || ''
                        ].filter(Boolean)
                      }
                    ]
                  })
                });

                const i18n = /** @type {any} */(window).SFQ_I18N || {};
                if (statusEl instanceof HTMLElement) {
                  if (res.ok) {
                    // Success: change button label instead of showing green text
                    if (saveBtn instanceof HTMLElement) {
                      saveBtn.textContent = i18n.save_success || 'データ保存が完了しました';
                    }
                    statusEl.textContent = '';
                    statusEl.removeAttribute('data-state');
                  } else {
                    const t = await res.text();
                    statusEl.textContent = i18n.save_error || '保存に失敗しました。時間を置いて再度お試しください。';
                    statusEl.dataset.state = 'error';
                    console.error('save error', res.status, t);
                    // Debug panel
                    let dbg = completeView && completeView.querySelector('.sfq-debug');
                    if (!(dbg instanceof HTMLElement) && completeView instanceof HTMLElement) {
                      dbg = document.createElement('details');
                      dbg.className = 'sfq-debug';
                      const sum = document.createElement('summary');
                      sum.textContent = i18n.debug_info || 'デバッグ情報';
                      const copy = document.createElement('button');
                      copy.type = 'button';
                      copy.className = 'sfq-debug__copy';
                      copy.textContent = i18n.copy || 'コピー';
                      const pre = document.createElement('pre');
                      pre.className = 'sfq-debug__pre';
                      dbg.appendChild(sum);
                      dbg.appendChild(copy);
                      dbg.appendChild(pre);
                      completeView.appendChild(dbg);
                      copy.addEventListener('click', () => {
                        navigator.clipboard?.writeText(pre.textContent || '');
                      });
                    }
                    if (dbg instanceof HTMLElement) {
                      const pre = dbg.querySelector('.sfq-debug__pre');
                      if (pre instanceof HTMLElement) {
                        const cfg = /** @type {any} */ (window).SFQ_CONFIG || {};
                        pre.textContent = JSON.stringify({
                          when: new Date().toISOString(),
                          origin: window.location.origin,
                          endpoint,
                          shopDomain,
                          customerId,
                          userAgent: navigator.userAgent,
                          config: { endpoint: cfg.endpoint, shopDomain: cfg.shopDomain, customerId: cfg.customerId },
                          status: res.status,
                          headers: Object.fromEntries(res.headers.entries()),
                          body: t
                        }, null, 2);
                      }
                      dbg.setAttribute('open', '');
                    }
                  }
                }
              } catch (err) {
                const i18nErr = /** @type {any} */(window).SFQ_I18N || {};
                if (statusEl instanceof HTMLElement) {
                  statusEl.textContent = i18nErr.save_network_error || '保存に失敗しました。通信環境をご確認ください。';
                  statusEl.dataset.state = 'error';
                }
                console.error('save exception', err);
                // Debug panel for exception
                let dbg = completeView && completeView.querySelector('.sfq-debug');
                if (!(dbg instanceof HTMLElement) && completeView instanceof HTMLElement) {
                  dbg = document.createElement('details');
                  dbg.className = 'sfq-debug';
                  const sum = document.createElement('summary');
                  sum.textContent = i18nErr.debug_info || 'デバッグ情報';
                  const copy = document.createElement('button');
                  copy.type = 'button';
                  copy.className = 'sfq-debug__copy';
                  copy.textContent = i18nErr.copy || 'コピー';
                  const pre = document.createElement('pre');
                  pre.className = 'sfq-debug__pre';
                  dbg.appendChild(sum);
                  dbg.appendChild(copy);
                  dbg.appendChild(pre);
                  completeView.appendChild(dbg);
                  copy.addEventListener('click', () => {
                    navigator.clipboard?.writeText(pre.textContent || '');
                  });
                }
                if (dbg instanceof HTMLElement) {
                  const pre = dbg.querySelector('.sfq-debug__pre');
                  if (pre instanceof HTMLElement) {
                    const cfg = /** @type {any} */ (window).SFQ_CONFIG || {};
                    pre.textContent = JSON.stringify({
                      when: new Date().toISOString(),
                      origin: window.location.origin,
                      endpoint,
                      shopDomain,
                      customerId,
                      userAgent: navigator.userAgent,
                      config: { endpoint: cfg.endpoint, shopDomain: cfg.shopDomain, customerId: cfg.customerId },
                      error: String(err)
                    }, null, 2);
                  }
                  dbg.setAttribute('open', '');
                }
              } finally {
                saveBtn.removeAttribute('aria-busy');
                saveBtn.removeAttribute('disabled');
              }
            }
          } catch (_) {}
        };
      }
    }

    // Show a small inline modal inside the dialog to require login before saving
    function showLoginRequiredAlert() {
      const d = dialog;
      if (!d) return;
      // Backdrop
      let backdrop = d.querySelector('.sfq-alert-backdrop');
      if (!(backdrop instanceof HTMLElement)) {
        backdrop = document.createElement('div');
        backdrop.className = 'sfq-alert-backdrop';
        d.appendChild(backdrop);
      }
      // Panel
      let panel = d.querySelector('.sfq-alert');
      if (!(panel instanceof HTMLElement)) {
        panel = document.createElement('div');
        panel.className = 'sfq-alert';
        const i18n = /** @type {any} */(window).SFQ_I18N || {};
        panel.innerHTML = `
          <div class="sfq-alert__body">
            <p class="sfq-alert__text">${i18n.login_required_alert || '保管するにはログインが必要です。'}</p>
            <div class="sfq-alert__actions">
              <a class="sfq-alert__login" href="/account/login">${i18n.log_in || 'ログイン'}</a>
              <button type="button" class="sfq-alert__close" aria-label="${i18n.close || '閉じる'}">${i18n.close || '閉じる'}</button>
            </div>
          </div>
        `;
        d.appendChild(panel);
        const closeBtn = panel.querySelector('.sfq-alert__close');
        if (closeBtn instanceof HTMLElement) closeBtn.addEventListener('click', hideLoginRequiredAlert);
        backdrop.addEventListener('click', hideLoginRequiredAlert);
      }
      if (backdrop instanceof HTMLElement) backdrop.hidden = false;
      if (panel instanceof HTMLElement) panel.hidden = false;
    }

    function hideLoginRequiredAlert() {
      const d = dialog;
      if (!d) return;
      const backdrop = d.querySelector('.sfq-alert-backdrop');
      const panel = d.querySelector('.sfq-alert');
      if (backdrop instanceof HTMLElement) backdrop.hidden = true;
      if (panel instanceof HTMLElement) panel.hidden = true;
    }

    function showModal() {
      // Ensure appended to body to escape any stacking context
      portalizeToBody();
      // Update close button event listeners after portalization
      updateCloseButtons();
      // prevent background scroll while modal open
      document.body.classList.add('sfq-no-scroll');
      if (dialog && !dialog.open) dialog.showModal();
      showStep(0);
    }

    function hideModal() {
      if (dialog && dialog.open) dialog.close();
      releaseFocus(modal);
      document.body.classList.remove('sfq-no-scroll');
    }

    // Selection handling per step (factored as function so we can rebind after portalization)
    function bindStepHandlers() {
      steps.forEach((stepEl, i) => {
        stepEl.addEventListener('click', (ev) => {
          const target = /** @type {Event} */(ev).target;
          if (!(target instanceof Element)) return;
          const btn = target.closest('.sfq-option');
          if (!btn) return;
          const all = stepEl.querySelectorAll('.sfq-option');
          all.forEach(b => b.classList.remove('is-selected'));
          btn.classList.add('is-selected');
          const val = btn.getAttribute('data-value') || '';
          // If we are in existing-shoe flow and this is step 1 now showing brands, treat as brand selection
          const i18nStep = /** @type {any} */(window).SFQ_I18N || {};
          if (i === 0 && (answers.q1 === i18nStep.method_shoe || answers.q1 === '今履いているシューズから診断') && matrixData) {
            answers.brand = val;
            console.debug('[SFQ] [Q1] brand selected:', answers.brand);
            // Update Q2 sizes for this brand and go to step 2
            configureStep2Size(answers.brand);
            showStep(1);
            return;
          }
          // Default behavior: store into q1/q2/q3 per original template
          answers[`q${i+1}`] = val;
          if (i === 0) {
            if (answers.q1 === i18nStep.method_foot || answers.q1 === '足のサイズで診断') {
              configureFlowForFootSize();
            } else if (answers.q1 === i18nStep.method_shoe || answers.q1 === '今履いているシューズから診断') {
              // Reconfigure step 1 to show brand/model and STAY on step 1
              configureFlowForExistingShoe();
              showStep(0);
              return; // prevent generic auto-advance to step 2
            }
          }
          // Auto-advance to next step or complete
          if (i < steps.length - 1) {
            showStep(i + 1);
          } else {
            showComplete();
          }
        });

        const next = stepEl.querySelector('[data-next]');
        const back = stepEl.querySelector('[data-back]');
        const finish = stepEl.querySelector('[data-finish]');

        next && next.addEventListener('click', () => {
          const selected = stepEl.querySelector('.sfq-option.is-selected');
          if (!selected) {
            stepEl.classList.add('needs-selection');
            setTimeout(() => stepEl.classList.remove('needs-selection'), 300);
            return;
          }
          if (i < steps.length - 1) {
            showStep(i + 1);
          } else {
            showComplete();
          }
        });

        back && back.addEventListener('click', () => {
          if (i > 0) showStep(i - 1);
        });

        finish && finish.addEventListener('click', () => {
          showComplete();
        });
      });
    }

    // Bind once for initial DOM
    bindStepHandlers();

    // Open/Close bindings
    openBtn.addEventListener('click', showModal);
    
    // Update close button references after portalization
    function updateCloseButtons() {
      if (dialog) {
        const dialogCloseBtns = dialog.querySelectorAll('[data-sfq-close]');
        dialogCloseBtns.forEach((b) => b.addEventListener('click', hideModal));
      }
    }

    // ESC to close (scoped)
    /** @param {KeyboardEvent} e */
    function escHandler(e) {
      if (e.key === 'Escape' && dialog && dialog.open) hideModal();
    }
    document.addEventListener('keydown', escHandler);

    anyC.__sfqInited = true;
  }

  // Focus trap helpers
  /** @param {HTMLElement} container */
  function trapFocus(container) {
    const anyC = /** @type {any} */(container);
    anyC.__sfqLastFocused = document.activeElement;
    const focusable = container.querySelectorAll('a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])');
    if (focusable.length) {
      const first = /** @type {HTMLElement} */(focusable[0]);
      first.focus();
    }
  }

  /** @param {HTMLElement} container */
  function releaseFocus(container) {
    const anyC = /** @type {any} */(container);
    const last = anyC.__sfqLastFocused;
    if (last && typeof last.focus === 'function') {
      last.focus();
    }
    anyC.__sfqLastFocused = null;
  }

  // Initialize all instances found on the page
  function initAll() {
    const candidates = document.querySelectorAll(
      '.size-fit-quiz, .size-fit-quiz-section, .size-fit-quiz-block, [id^="size-fit-quiz-"], [id^="size-fit-quiz-block-"]'
    );
    if (candidates.length) {
      candidates.forEach((el) => {
        const host = el instanceof HTMLElement ? el : (/** @type {Element} */(el)).parentElement;
        if (host instanceof HTMLElement) initOne(host);
      });
    } else {
      // Fallback: init around each open button
      document.querySelectorAll('[data-sfq-open]').forEach(btn => {
        const found = btn.closest('.size-fit-quiz, .size-fit-quiz-section, .size-fit-quiz-block, [id^="size-fit-quiz-"], [id^="size-fit-quiz-block-"]');
        const container = found instanceof HTMLElement ? found : document.body;
        initOne(container);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }
})();
